import { createContext, useContext, useState, useEffect, ReactNode, useCallback, useRef } from "react";
import { useLocation } from "react-router-dom";
import { toast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { storageKeys, safeStorage } from "@/lib/storage";
import { calculateBuyerServiceFee } from "@/lib/ticket-pricing";

export interface CartItem {
  tierId: string;
  eventId: string;
  tierName: string;
  eventTitle: string;
  eventSlug: string;
  producerUsername?: string | null;
  price: number;
  quantity: number;
  coverImageUrl?: string;
  platformFeePercent?: number;
  maxPerOrder?: number;
  absorbFee?: boolean;
}

interface CartContextType {
  items: CartItem[];
  addItem: (item: CartItem) => void;
  removeItem: (tierId: string) => void;
  updateQuantity: (tierId: string, quantity: number) => void;
  clearCart: () => void;
  renewExpiry: () => void;
  subtotal: number;
  platformFee: number;
  total: number;
  itemCount: number;
  expiresAt: Date | null;
  startCheckout: () => void;
  couponCode: string;
  setCouponCode: (code: string) => void;
  discount: number;
  setDiscount: (amount: number) => void;
  appliedCouponId: string | null;
  setAppliedCouponId: (id: string | null) => void;
  finalTotal: number;
  trackingCode: string | null;
  setTrackingCode: (code: string | null) => void;
}

const CART_DURATION_MS = 15 * 60 * 1000;

const CartContext = createContext<CartContextType>({
  items: [], addItem: () => {}, removeItem: () => {}, updateQuantity: () => {},
  clearCart: () => {}, renewExpiry: () => {}, subtotal: 0, platformFee: 0, total: 0, itemCount: 0,
  expiresAt: null, startCheckout: () => {}, couponCode: "", setCouponCode: () => {},
  discount: 0, setDiscount: () => {}, appliedCouponId: null, setAppliedCouponId: () => {},
  finalTotal: 0, trackingCode: null, setTrackingCode: () => {},
});

export const useCart = () => useContext(CartContext);

function loadCart(userId: string | null): CartItem[] {
  const stored = safeStorage.get(storageKeys.cart(userId));
  if (!stored) return [];
  try {
    return JSON.parse(stored);
  } catch {
    return [];
  }
}

function loadExpiry(userId: string | null): Date | null {
  const stored = safeStorage.get(storageKeys.cartExpiry(userId));
  if (!stored) return null;
  try {
    const date = new Date(stored);
    return date.getTime() > Date.now() ? date : null;
  } catch {
    return null;
  }
}

export function CartProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const userId = user?.id ?? null;

  const [items, setItems] = useState<CartItem[]>(() => loadCart(userId));
  const [expiresAt, setExpiresAt] = useState<Date | null>(() => loadExpiry(userId));
  const [couponCode, setCouponCode] = useState("");
  const [discount, setDiscount] = useState(0);
  const [appliedCouponId, setAppliedCouponId] = useState<string | null>(null);
  const [trackingCode, setTrackingCode] = useState<string | null>(() => {
    return safeStorage.get("promoter_tracking_code", sessionStorage);
  });
  const location = useLocation();

  // Watch for ref param in URL
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const ref = params.get("ref");
    if (ref) {
      console.log("[CartContext] Tracking code captured from URL:", ref);
      setTrackingCode(ref);
      safeStorage.set("promoter_tracking_code", ref, sessionStorage);
    }
  }, [location.search]);

  // Track previous userId to detect login (null → uuid)
  const prevUserIdRef = useRef<string | null>(null);

  // Reload cart when user changes (login / logout / switch account)
  useEffect(() => {
    const prevUserId = prevUserIdRef.current;
    prevUserIdRef.current = userId;

    // Migrate anonymous cart to logged-in user on login
    if (!prevUserId && userId) {
      const anonymousItems = loadCart(null);
      const anonymousExpiry = loadExpiry(null);
      if (anonymousItems.length > 0) {
        safeStorage.set(storageKeys.cart(userId), JSON.stringify(anonymousItems));
        if (anonymousExpiry) {
          safeStorage.set(storageKeys.cartExpiry(userId), anonymousExpiry.toISOString());
        }
        // Clear anonymous cart to prevent duplication on future logins
        safeStorage.remove(storageKeys.cart(null));
        safeStorage.remove(storageKeys.cartExpiry(null));
        setItems(anonymousItems);
        setExpiresAt(anonymousExpiry);
        return;
      }
    }

    setItems(loadCart(userId));
    setExpiresAt(loadExpiry(userId));
  }, [userId]);

  useEffect(() => {
    safeStorage.set(storageKeys.cart(userId), JSON.stringify(items));
    if (items.length === 0) {
      safeStorage.remove(storageKeys.cartExpiry(userId));
      setExpiresAt(null);
      setCouponCode("");
      setDiscount(0);
      setAppliedCouponId(null);
    }
  }, [items, userId]);

  useEffect(() => {
    if (!expiresAt) return;
    const timeout = setTimeout(() => {
      if (Date.now() >= expiresAt.getTime()) {
        setItems([]);
        setExpiresAt(null);
        safeStorage.remove(storageKeys.cart(userId));
        safeStorage.remove(storageKeys.cartExpiry(userId));
      }
    }, Math.max(0, expiresAt.getTime() - Date.now()));
    return () => clearTimeout(timeout);
  }, [expiresAt, userId]);

  // A10: Validate maxPerOrder before adding
  const addItem = useCallback((item: CartItem) => {
    // Invalidate any pending checkout order when cart changes
    safeStorage.remove(storageKeys.checkoutOrderId(userId), sessionStorage);
    setItems((prev) => {
      if (prev.length > 0 && prev[0].eventId !== item.eventId) {
        safeStorage.remove(storageKeys.cart(userId));
        // Avoid noisy toast for cart replacement — this can be surprising when frequent.
        // Keep behavior: replace cart with new event's item silently.
        return [item];
      }
      const existing = prev.find((i) => i.tierId === item.tierId);
      if (existing) {
        const maxAllowed = item.maxPerOrder ?? 10;
        const newQty = Math.min(existing.quantity + item.quantity, maxAllowed);
        if (newQty === existing.quantity) {
          toast({ title: "Limite atingido", description: `Máximo de ${maxAllowed} ingressos deste tipo por pedido.` });
          return prev;
        }
        return prev.map((i) =>
          i.tierId === item.tierId ? { ...i, quantity: newQty } : i
        );
      }
      return [...prev, item];
    });
    if (!expiresAt) {
      const expiry = new Date(Date.now() + CART_DURATION_MS);
      setExpiresAt(expiry);
      safeStorage.set(storageKeys.cartExpiry(userId), expiry.toISOString());
    }
  }, [expiresAt]);

  const removeItem = useCallback((tierId: string) => {
    // Invalidate any pending checkout order when cart changes
    safeStorage.remove(storageKeys.checkoutOrderId(userId), sessionStorage);
    setItems((prev) => prev.filter((i) => i.tierId !== tierId));
  }, [userId]);

  const updateQuantity = useCallback((tierId: string, quantity: number) => {
    // Invalidate any pending checkout order when cart changes
    safeStorage.remove(storageKeys.checkoutOrderId(userId), sessionStorage);
    if (quantity <= 0) { removeItem(tierId); return; }
    setItems((prev) => prev.map((i) => (i.tierId === tierId ? { ...i, quantity } : i)));
  }, [removeItem, userId]);

  const clearCart = useCallback(() => {
    setItems([]);
    setExpiresAt(null);
    setCouponCode("");
    setDiscount(0);
    setAppliedCouponId(null);
    safeStorage.remove(storageKeys.cart(userId));
    safeStorage.remove(storageKeys.cartExpiry(userId));
    // Invalidate any pending checkout order when cart is cleared
    safeStorage.remove(storageKeys.checkoutOrderId(userId), sessionStorage);
  }, [userId]);

  const renewExpiry = useCallback(() => {
    if (items.length === 0) return;
    const expiry = new Date(Date.now() + CART_DURATION_MS);
    setExpiresAt(expiry);
    safeStorage.set(storageKeys.cartExpiry(userId), expiry.toISOString());
  }, [items.length, userId]);

  const subtotal = items.reduce((sum, i) => sum + i.price * i.quantity, 0);
  const platformFee = (subtotal === 0 || discount >= subtotal) ? 0 : items.reduce((sum, i) => {
    if (i.absorbFee) return sum;
    return sum + calculateBuyerServiceFee(i.price * i.quantity, i.platformFeePercent);
  }, 0);
  const total = subtotal + platformFee;
  const finalTotal = Math.max(0, total - discount);
  const itemCount = items.reduce((sum, i) => sum + i.quantity, 0);

  const startCheckout = useCallback(() => {
    if (!expiresAt) {
      const expiry = new Date(Date.now() + CART_DURATION_MS);
      setExpiresAt(expiry);
      safeStorage.set(storageKeys.cartExpiry(userId), expiry.toISOString());
    }
  }, [expiresAt]);

  return (
    <CartContext.Provider
      value={{
        items, addItem, removeItem, updateQuantity, clearCart, renewExpiry, subtotal, platformFee,
        total, itemCount, expiresAt, startCheckout, couponCode, setCouponCode,
        discount, setDiscount, appliedCouponId, setAppliedCouponId, finalTotal,
        trackingCode,
        setTrackingCode: (code: string | null) => {
          setTrackingCode(code);
          if (code) safeStorage.set("promoter_tracking_code", code, sessionStorage);
          else safeStorage.remove("promoter_tracking_code", sessionStorage);
        },
      }}
    >
      {children}
    </CartContext.Provider>
  );
}

